import React, { useState, useEffect } from 'react';
import { Menu, X, UtensilsCrossed, Home, MessageCircle, Globe, Phone } from 'lucide-react';
import { handleSmoothScroll } from '../../utils/scrollUtils';
import Logo1 from '../../assets/images/logo1.png';
import BookingModal from './BookingModal';

const Header = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [activeSection, setActiveSection] = useState('home');
    const [isScrolled, setIsScrolled] = useState(false);
    const [showBookingModal, setShowBookingModal] = useState(false);
    const [language, setLanguage] = useState(() => {
        // Check if language is saved in localStorage
        const savedLanguage = localStorage.getItem('language');
        return savedLanguage || 'en'; // Default to English
    });
    const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);

    const menuCategories = [
        'North Indian',
        'South Indian',
        'Indo-Chinese',
        'Vegetarian',
        'Vegan Options',
        'Weekly Meal Plans',
        'Corporate Plans'
    ];

    // Translation object
    const translations = {
        en: {
            home: 'Home',
            menu: 'Menu',
            testimonials: 'Testimonials',
            contact: 'Contact',
            orderNow: 'Order Now',
            whatsappUs: 'WhatsApp Us',
            callUs: 'Call Us',
            language: 'Language'
        },
        de: {
            home: 'Startseite',
            menu: 'Speisekarte',
            testimonials: 'Bewertungen',
            contact: 'Kontakt',
            orderNow: 'Jetzt Bestellen',
            whatsappUs: 'WhatsApp Uns',
            callUs: 'Ruf Uns An',
            language: 'Sprache'
        }
    };

    // Use the current language for text
    const t = translations[language];

    useEffect(() => {
        const handleScroll = () => {
            // Update header style when scrolled
            setIsScrolled(window.scrollY > 50);

            // Update active section based on scroll position
            const sections = document.querySelectorAll('section[id]');
            let current = '';

            sections.forEach(section => {
                const sectionTop = section.offsetTop - 100;
                if (window.scrollY >= sectionTop) {
                    current = section.getAttribute('id');
                }
            });

            if (current && current !== activeSection) {
                setActiveSection(current);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [activeSection]);

    useEffect(() => {
        // Save language preference to localStorage
        localStorage.setItem('language', language);
        // Update html lang attribute for accessibility
        document.documentElement.lang = language;

        // Close language menu when clicking outside
        const handleClickOutside = (event) => {
            if (isLanguageMenuOpen && !event.target.closest('.language-switcher')) {
                setIsLanguageMenuOpen(false);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [language, isLanguageMenuOpen]);

    const toggleLanguage = (lang) => {
        setLanguage(lang);
        setIsLanguageMenuOpen(false);
    };
    // Navigation Items
    const navItems = [
        { id: 'home', label: t.home, icon: <Home size={18} /> },
        { id: 'menu', label: t.menu, icon: <UtensilsCrossed size={18} /> },
        { id: 'testimonials', label: t.testimonials, icon: <MessageCircle size={18} /> },
        { id: 'contact', label: t.contact, icon: <Phone size={18} /> }
    ];

    return (
        <header className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-zinc-900 text-white' : 'bg-zinc-800 text-white'
            } py-3`}>
            <div className="container mx-auto px-4">
                <nav className="flex items-center justify-between">
                    <div className="flex items-center">
                        <div className="mr-6 flex items-start space-x-1">
                            <img
                                src={Logo1}
                                alt="Berlin Tiffin"
                                className="w-12 h-auto object-contain"
                            />
                            <div>
                                <h3 className="text-2xl font-bold tracking-wider uppercase leading-tight">
                                    <span style={{ color: '#7bae37' }}>Berlin</span>
                                    <span className="text-white">Tiffin</span>
                                </h3>
                                {/* <p className="text-sm text-gray-300 mt-1">Powered by Swadistha Restaurant</p> */}
                            </div>
                        </div>

                        <ul className="hidden md:flex space-x-8">
                            {navItems.map(item => (
                                <li key={item.id}>
                                    <a
                                        href={`#${item.id}`}
                                        onClick={(e) => handleSmoothScroll(e)}
                                        className={`font-medium transition-colors duration-300 flex items-center gap-2 ${activeSection === item.id
                                            ? ''
                                            : 'text-gray-200'
                                            }`}
                                        style={activeSection === item.id ? { color: '#7bae37' } : { '&:hover': { color: '#7bae37' } }}
                                    >
                                        {item.icon}
                                        {item.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="hidden md:flex items-center space-x-4">
                        <button
                            className="px-5 py-2 rounded-none font-medium uppercase tracking-wider transition-colors duration-300 text-zinc-900 border-l-4 border-zinc-700"
                            style={{ backgroundColor: '#7bae37' }}
                            onClick={() => setShowBookingModal(true)}
                        >
                            {t.orderNow}
                        </button>
                    </div>
                    <button
                        className="md:hidden transition-colors text-gray-200"
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                    >
                        {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </nav>
            </div>

            {/* Mobile Menu */}
            {isMenuOpen && (
                <div className="md:hidden bg-zinc-800 border-t border-zinc-700">
                    <ul className="py-4 px-6 space-y-4">
                        {navItems.map(item => (
                            <li key={item.id}>
                                <a
                                    href={`#${item.id}`}
                                    className={`block font-medium flex items-center gap-2 ${activeSection === item.id ? '' : 'text-gray-200'
                                        }`}
                                    style={activeSection === item.id ? { color: '#7bae37' } : {}}
                                    onClick={(e) => {
                                        handleSmoothScroll(e, () => setIsMenuOpen(false));
                                    }}
                                >
                                    {item.icon}
                                    {item.label}
                                </a>
                            </li>
                        ))}
                        <li>
                            <button className="w-full px-5 py-2 text-zinc-900 rounded-none font-medium uppercase tracking-wider transition-colors border-l-4 border-zinc-700"
                                style={{ backgroundColor: '#7bae37' }}
                                onClick={() => {
                                    setIsMenuOpen(false);
                                    setShowBookingModal(true);
                                }}
                            >
                                {t.orderNow}
                            </button>
                        </li>
                    </ul>
                </div>
            )}

            {/* Booking Modal */}
            <BookingModal
                isOpen={showBookingModal}
                onClose={() => setShowBookingModal(false)}
                menuCategories={menuCategories}
                language={language}
            />
        </header>
    );
};

export default Header;